import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ViewToggle } from "@/components/shared/ViewToggle";
import { Skeleton } from "@/components/ui/skeleton";
import { CountryVehicleMappingTable } from "@/components/country-vehicle-mapping/CountryVehicleMappingTable";

interface CountryVehicleMapping {
  id: string;
  country_id: string;
  vehicle_type_id: string;
  passenger_count: number;
  luggage_count: number;
  countries: { name_ar: string };
  vehicle_types: { name_ar: string };
}

export default function CountryVehicleMapping() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CountryVehicleMapping | null>(null);
  const [formData, setFormData] = useState({
    country_id: "",
    vehicle_type_id: "",
    passenger_count: 1,
    luggage_count: 1
  });
  const [view, setView] = useState<'cards' | 'table'>('cards');
  const queryClient = useQueryClient();

  const { data: mappings = [], isLoading } = useQuery({
    queryKey: ['country-vehicle-mapping'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('country_vehicle_mapping')
        .select(`
          *,
          countries(name_ar),
          vehicle_types(name_ar)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as CountryVehicleMapping[];
    }
  });

  const { data: countries = [] } = useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('countries')
        .select('id, name_ar')
        .order('name_ar');
      
      if (error) throw error;
      return data;
    }
  });

  const { data: vehicleTypes = [] } = useQuery({
    queryKey: ['vehicle-types-select'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicle_types')
        .select('id, name_ar')
        .order('sort_order');
      
      if (error) throw error;
      return data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase
        .from('country_vehicle_mapping')
        .insert([data]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['country-vehicle-mapping'] });
      toast.success("تم إضافة الربط بنجاح");
      resetForm();
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء إضافة الربط");
      console.error(error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase
        .from('country_vehicle_mapping')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['country-vehicle-mapping'] });
      toast.success("تم تحديث الربط بنجاح");
      resetForm();
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء تحديث الربط");
      console.error(error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('country_vehicle_mapping')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['country-vehicle-mapping'] });
      toast.success("تم حذف الربط بنجاح");
    },
    onError: (error) => {
      toast.error("حدث خطأ أثناء حذف الربط");
      console.error(error);
    }
  });

  const resetForm = () => {
    setFormData({ country_id: "", vehicle_type_id: "", passenger_count: 1, luggage_count: 1 });
    setEditingItem(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (item: CountryVehicleMapping) => {
    setEditingItem(item);
    setFormData({
      country_id: item.country_id,
      vehicle_type_id: item.vehicle_type_id,
      passenger_count: item.passenger_count,
      luggage_count: item.luggage_count
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm("هل أنت متأكد من حذف هذا الربط؟")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold">ربط السيارات بالدولة</h1>
        
        <ViewToggle view={view} onViewChange={setView} />
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              setEditingItem(null);
              setFormData({ country_id: "", vehicle_type_id: "", passenger_count: 1, luggage_count: 1 });
            }}>
              <Plus className="w-4 h-4 mr-2" />
              إضافة ربط جديد
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingItem ? "تعديل الربط" : "إضافة ربط جديد"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="country_id">الدولة</Label>
                <Select value={formData.country_id} onValueChange={(value) => setFormData({ ...formData, country_id: value })} required>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر الدولة" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.id} value={country.id}>
                        {country.name_ar}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="vehicle_type_id">نوع السيارة</Label>
                <Select value={formData.vehicle_type_id} onValueChange={(value) => setFormData({ ...formData, vehicle_type_id: value })} required>
                  <SelectTrigger>
                    <SelectValue placeholder="اختر نوع السيارة" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name_ar}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="passenger_count">عدد المسافرين</Label>
                <Input
                  id="passenger_count"
                  type="number"
                  min="1"
                  value={formData.passenger_count}
                  onChange={(e) => setFormData({ ...formData, passenger_count: parseInt(e.target.value) || 1 })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="luggage_count">عدد الحقائب</Label>
                <Input
                  id="luggage_count"
                  type="number"
                  min="0"
                  value={formData.luggage_count}
                  onChange={(e) => setFormData({ ...formData, luggage_count: parseInt(e.target.value) || 0 })}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>
                  إلغاء
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? 'جاري الحفظ...' : (editingItem ? "تحديث" : "إضافة")}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        view === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-[200px] w-full" />)}
          </div>
        ) : (
          <Skeleton className="h-[400px] w-full rounded-lg" />
        )
      ) : view === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {mappings.map((mapping) => (
            <Card key={mapping.id}>
              <CardHeader>
                <CardTitle>{mapping.countries?.name_ar}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p><strong>نوع السيارة:</strong> {mapping.vehicle_types?.name_ar}</p>
                  <p><strong>عدد المسافرين:</strong> {mapping.passenger_count}</p>
                  <p><strong>عدد الحقائب:</strong> {mapping.luggage_count}</p>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={() => handleEdit(mapping)}>
                    تعديل
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    onClick={() => handleDelete(mapping.id)}
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? 'جاري الحذف...' : 'حذف'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <CountryVehicleMappingTable 
          mappings={mappings} 
          onEdit={handleEdit} 
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending} 
        />
      )}
      
      {mappings.length === 0 && !isLoading && (
        <div className="text-center py-12 text-gray-500">
          <p>لم يتم العثور على أي ارتباطات.</p>
        </div>
      )}
    </div>
  );
}
